/**
 * The parts of a Made-to-Measure order: one row per part with its editable
 * fields, status menu, PDF actions, and a collapsible row holding the timeline
 * or the measurements.
 */
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronCircleDown,
  faChevronDown,
  faChevronRight,
  faCircleNotch,
  faClock,
  faCloudUploadAlt,
  faDownload,
  faExclamationTriangle,
  faInfoCircle,
  faPencilAlt,
} from '@fortawesome/free-solid-svg-icons';
import { PLATFORM, ROLES, type Role } from '@/shared/constants';
import measureIcon from '@/assets/images/measure.png';
import measureWithIndicatorIcon from '@/assets/images/measureWithIndicator.png';
import type { Order } from '../api';
import {
  CS,
  ORDER_TYPES,
  STATUS_COLORS,
  statusColor,
  statusLabel,
} from '../order-types';
import { formatLocalDate, nextStatusIds } from '../logic';
import { HorizontalTimeline } from './HorizontalTimeline';
import {
  anyMeasurementOutOfLimit,
  deliveryDateSoon,
  fabricCode,
  isDmsEditable,
  isUnknownFabricCode,
  measureImageUrl,
  measurementOutOfLimit,
  partStatusChangeAllowed,
  partTimelineItems,
  withSign,
  type DmsField,
  type Measurement,
  type OrderPart,
} from './dms-logic';

export interface DmsPartsTableProps {
  order: Order;
  role: Role;
  groups: readonly (readonly OrderPart[])[];
  isEditMode: boolean;
  updatingParts: Readonly<Record<string, boolean>>;
  invalidParts: ReadonlySet<string>;
  fabricCodes: ReadonlySet<string> | undefined;
  printing: boolean;
  uploadDisabled: (group: readonly OrderPart[]) => boolean;
  onEnterEdit: () => void;
  onPartField: (partId: string, field: string, value: string) => void;
  onPartFlag: (partId: string, field: string, value: boolean) => void;
  onFabric: (partId: string, code: string) => void;
  onMeasurement: (
    partId: string,
    index: number,
    patch: Partial<Measurement>,
  ) => void;
  onPartStatus: (part: OrderPart, target: number) => void;
  onOpenPart: (part: OrderPart) => void;
  onDownloadPdf: (group: readonly OrderPart[]) => void;
  onUploadPdf: (group: readonly OrderPart[]) => void;
}

const localDate = (value: string | null | undefined) =>
  value ? formatLocalDate(new Date(value)) : '-';

export function DmsPartsTable({
  order,
  role,
  groups,
  isEditMode,
  updatingParts,
  invalidParts,
  fabricCodes,
  printing,
  uploadDisabled,
  onEnterEdit,
  onPartField,
  onPartFlag,
  onFabric,
  onMeasurement,
  onPartStatus,
  onOpenPart,
  onDownloadPdf,
  onUploadPdf,
}: Readonly<DmsPartsTableProps>) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [panel, setPanel] = useState<
    Record<string, 'timeline' | 'measurements'>
  >({});
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const isAdmin = role === ROLES.ADMIN;
  const editable = (field: DmsField, part?: OrderPart) =>
    isDmsEditable(role, order, field, part, updatingParts);

  const pencil = (
    field: DmsField,
    part: OrderPart,
    label: string,
    testId?: string,
  ) =>
    editable(field, part) && (
      <button
        type="button"
        className="pencil"
        aria-label={`Edit ${label}`}
        data-testid={testId}
        onClick={onEnterEdit}
      >
        <FontAwesomeIcon icon={faPencilAlt} />
      </button>
    );

  const textCell = (
    part: OrderPart,
    field: DmsField & `part${string}`,
    key: string,
    label: string,
    display: ReactNode,
    value: string,
    onChange: (value: string) => void,
    extra: { type?: string; invalid?: boolean; testId?: string } = {},
  ) => (
    <>
      <span className="view-item">
        {display}{' '}
        {pencil(
          field,
          part,
          label,
          extra.testId ? `${extra.testId}-edit-button` : undefined,
        )}
      </span>
      <input
        type={extra.type ?? 'text'}
        className={`field part-field form-control edit-item ${extra.invalid ? 'error' : ''}`}
        name={`${key}-${part.id}`}
        aria-label={label}
        data-testid={extra.testId ? `${extra.testId}-input` : undefined}
        readOnly={!editable(field, part)}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </>
  );

  const statusMenu = (part: OrderPart) => (
    <td
      style={{ width: '10%', color: statusColor(part.customisationStatusId) }}
      className="status-cell"
      data-testid="mtm-part-row-status"
    >
      {statusLabel(part.customisationStatusId)}
      <button
        type="button"
        className="pointer-cursor ml-1 menu-toggle"
        aria-label={`Change status of ${part.model.data.productLine ?? 'part'}`}
        aria-haspopup="menu"
        aria-expanded={openMenu === part.id}
        data-testid="mtm-part-row-status-button"
        onClick={() => setOpenMenu(openMenu === part.id ? null : part.id)}
      >
        <FontAwesomeIcon
          icon={faChevronCircleDown}
          style={{ color: 'black' }}
        />
      </button>
      {openMenu === part.id && (
        <div className="dropdown-menu show" role="menu">
          {isEditMode && <h5>Please save to change order&apos;s status</h5>}
          {nextStatusIds(PLATFORM.DMS, role, part.customisationStatusId).map(
            (number) => (
              <button
                key={number}
                type="button"
                role="menuitem"
                className="dropdown-item"
                style={{ color: STATUS_COLORS[number] }}
                data-testid="mtm-part-row-status-option"
                data-value={number}
                disabled={
                  !partStatusChangeAllowed(order, part, number, isEditMode) ||
                  !!updatingParts[part.id]
                }
                onClick={() => {
                  setOpenMenu(null);
                  onPartStatus(part, number);
                }}
              >
                <div
                  className="status-point"
                  style={{ background: STATUS_COLORS[number] }}
                />
                <div className="no-select">{ORDER_TYPES[number]}</div>
              </button>
            ),
          )}
        </div>
      )}
    </td>
  );

  const pdfCells = (
    group: readonly OrderPart[],
    part: OrderPart,
    first: boolean,
  ) => {
    const canUpload =
      first &&
      order.sfCartId != null &&
      order.customisationStatusId === CS.REQUEST_SENT;
    return (
      <>
        <td style={{ width: '7%' }} data-testid="mtm-part-row-download-pdf">
          {first && (
            <button
              type="button"
              className="icon-button"
              aria-label="Download the purchase order"
              data-testid="mtm-part-row-download-pdf-button"
              disabled={printing}
              onClick={() => onDownloadPdf(group)}
            >
              <FontAwesomeIcon
                icon={printing ? faCircleNotch : faDownload}
                spin={printing}
                style={{ fontSize: 24 }}
              />
            </button>
          )}
        </td>
        <td style={{ width: '7%' }} data-testid="mtm-part-row-upload-pdf">
          {canUpload && (
            <button
              type="button"
              className="icon-button upload-pdf"
              aria-label={`Upload the ${part.model.data.productLine === 'SHIRT' ? 'shirt' : 'suiting'} PDF`}
              data-testid="mtm-part-row-upload-pdf-button"
              disabled={uploadDisabled(group) || printing}
              onClick={() => onUploadPdf(group)}
            >
              <FontAwesomeIcon
                icon={printing ? faCircleNotch : faCloudUploadAlt}
                spin={printing}
                style={{ fontSize: 24 }}
              />
            </button>
          )}
        </td>
      </>
    );
  };

  const measurementsPanel = (part: OrderPart) => (
    <>
      <button
        type="button"
        className="pointer-cursor flex items-center mr-4 panel-switch"
        onClick={() => setPanel({ ...panel, [part.id]: 'timeline' })}
        aria-label="Show the timeline"
        data-testid="mtm-part-details-timeline-button"
      >
        <FontAwesomeIcon
          icon={faChevronRight}
          style={{ fontSize: 30, transform: 'rotate(180deg)' }}
        />
        <div className="ml-2 text-center">
          <FontAwesomeIcon icon={faClock} style={{ fontSize: 25 }} />
          <div>Timeline</div>
        </div>
      </button>
      <div className="measurements" data-testid="mtm-part-details-measurements">
        {(part.measurements ?? []).map((measurement, index) => (
          <MeasurementCard
            key={`${measurement.name}-${index}`}
            part={part}
            measurement={measurement}
            index={index}
            editable={editable('measurements')}
            isEditMode={isEditMode}
            onChange={(patch) => onMeasurement(part.id, index, patch)}
          />
        ))}
      </div>
    </>
  );

  const detailsRow = (part: OrderPart) => (
    <tr key={`${part.id}-details`} className="part-details-row">
      <td colSpan={isAdmin ? 14 : 13} style={{ padding: 0 }}>
        <div className="part-collapse p-6">
          <div className="flex justify-between items-center">
            {panel[part.id] === 'measurements' ? (
              measurementsPanel(part)
            ) : (
              <>
                <div
                  style={{ overflowX: 'auto', overflowY: 'hidden' }}
                  data-testid="mtm-part-details-timeline"
                >
                  <HorizontalTimeline items={partTimelineItems(part, order)} />
                </div>
                <button
                  type="button"
                  className="pointer-cursor flex items-center ml-4 panel-switch"
                  onClick={() =>
                    setPanel({ ...panel, [part.id]: 'measurements' })
                  }
                  aria-label="Show the measurements"
                  data-testid="mtm-part-details-measurements-button"
                >
                  <img
                    src={
                      anyMeasurementOutOfLimit(part)
                        ? measureWithIndicatorIcon
                        : measureIcon
                    }
                    className="mr-2"
                    alt=""
                    style={{ height: 70 }}
                  />
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    style={{ fontSize: 30 }}
                  />
                </button>
              </>
            )}
          </div>
        </div>
      </td>
    </tr>
  );

  const partRow = (
    group: readonly OrderPart[],
    part: OrderPart,
    index: number,
  ) => {
    const code = fabricCode(part);
    const line = String(part.model.data.productLine ?? '');
    return (
      <tr key={part.id} className="part-row">
        <td
          style={{ width: '10%', whiteSpace: 'nowrap' }}
          data-testid="mtm-part-row-category"
        >
          {line.replace('_', ' ')}
          <button
            type="button"
            className={`pointer-cursor btn collapse-rotate ${expanded[part.id] ? '' : 'collapsed'}`}
            aria-expanded={!!expanded[part.id]}
            aria-label={`Toggle the details of ${line}`}
            data-testid="mtm-part-row-toggle-button"
            onClick={() =>
              setExpanded({ ...expanded, [part.id]: !expanded[part.id] })
            }
          >
            <FontAwesomeIcon icon={faChevronDown} />
          </button>
          <button
            type="button"
            className="icon-button cp"
            aria-label={`Summary of ${line}`}
            data-testid="mtm-part-row-details-button"
            onClick={() => onOpenPart(part)}
          >
            <FontAwesomeIcon icon={faInfoCircle} />
          </button>
        </td>
        <td style={{ width: '12%' }} data-testid="mtm-part-row-sku">
          {textCell(
            part,
            'partSku',
            'partSku',
            t('orders.sku'),
            part.sku || '-',
            String(part.sku ?? ''),
            (value) => onPartField(part.id, 'sku', value),
            { testId: 'mtm-part-row-sku' },
          )}
        </td>
        <td style={{ width: '9%' }} data-testid="mtm-part-row-fabric">
          <span className="view-item">
            {code || '-'}
            {isUnknownFabricCode(code, fabricCodes) && (
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="mr-1"
                style={{ color: 'darkorange' }}
                aria-label="Unknown fabric code"
              />
            )}
            {pencil(
              'partFabric',
              part,
              t('orders.fabric'),
              'mtm-part-row-fabric-edit-button',
            )}
          </span>
          <input
            type="text"
            className="field part-field form-control edit-item"
            name={`partFabric-${part.id}`}
            aria-label={t('orders.fabric')}
            data-testid="mtm-part-row-fabric-input"
            readOnly={!editable('partFabric', part)}
            value={code ?? ''}
            onChange={(event) => onFabric(part.id, event.target.value)}
          />
        </td>
        <td style={{ width: '9%' }} data-testid="mtm-part-row-size">
          <div>
            {textCell(
              part,
              'partSize',
              'partSize',
              t('orders.size'),
              `${part.selected.sizePants != null ? 'JACKET ' : ''}${String(part.selected.size ?? '') || '-'}`,
              String(part.selected.size ?? ''),
              (value) => onPartField(part.id, 'selected.size', value),
              {
                invalid: invalidParts.has(part.id),
                testId: 'mtm-part-row-size',
              },
            )}
          </div>
          {part.selected.sizePants != null && (
            <div>
              {textCell(
                part,
                'partSize',
                'partSizePants',
                'Pants size',
                `PANTS ${String(part.selected.sizePants) || '-'}`,
                String(part.selected.sizePants ?? ''),
                (value) => onPartField(part.id, 'selected.sizePants', value),
                { testId: 'mtm-part-row-size-pants' },
              )}
            </div>
          )}
        </td>
        <td style={{ width: '8%' }} data-testid="mtm-part-row-count">
          {textCell(
            part,
            'partCount',
            'partCount',
            t('orders.quantity'),
            part.count || '-',
            part.count == null ? '' : String(part.count),
            (value) => onPartField(part.id, 'count', value),
            {
              type: 'number',
              invalid: invalidParts.has(part.id),
              testId: 'mtm-part-row-count',
            },
          )}
        </td>
        <td style={{ width: '10%' }} data-testid="mtm-part-row-local-price">
          {textCell(
            part,
            'partLocalPrice',
            'partLocalPrice',
            t('orders.localPrice'),
            part.localPrice || '-',
            part.localPrice == null ? '' : String(part.localPrice),
            (value) => onPartField(part.id, 'localPrice', value),
            { testId: 'mtm-part-row-local-price' },
          )}
        </td>
        {isAdmin && (
          <td style={{ width: '10%' }} data-testid="mtm-part-row-price">
            {textCell(
              part,
              'partPrice',
              'partPrice',
              t('orders.unitaryPriceEur'),
              part.price || '-',
              part.price == null ? '' : String(part.price),
              (value) => onPartField(part.id, 'price', value),
              { testId: 'mtm-part-row-price' },
            )}
          </td>
        )}
        <td
          style={{ width: '18%' }}
          data-testid="mtm-part-row-supplier-delivery-date"
        >
          {textCell(
            part,
            'partSupplierExpectedDeliveryDate',
            'partSupplierExpectedDeliveryDate',
            t('orders.supplierExpectedDeliveryDate'),
            localDate(part.supplierExpectedDeliveryDate),
            part.supplierExpectedDeliveryDate ?? '',
            (value) =>
              onPartField(part.id, 'supplierExpectedDeliveryDate', value),
            { type: 'date', testId: 'mtm-part-row-supplier-delivery-date' },
          )}
        </td>
        <td
          style={{
            width: '15%',
            color: deliveryDateSoon(part.expectedDeliveryDate)
              ? 'darkorange'
              : 'inherit',
          }}
          data-testid="mtm-part-row-delivery-date"
        >
          {localDate(part.expectedDeliveryDate)}
        </td>
        <td style={{ width: '10%' }} data-testid="mtm-part-row-oa">
          {textCell(
            part,
            'partOaCode',
            'partOaCode',
            'OA',
            part.oaCode || '-',
            part.oaCode ?? '',
            (value) => onPartField(part.id, 'oaCode', value),
            { testId: 'mtm-part-row-oa' },
          )}
        </td>
        <td style={{ width: '10%' }} data-testid="mtm-part-row-quality-issue">
          <div className="styled-input-square inline-flex">
            <div className="styled-input-single" style={{ marginRight: 10 }}>
              <input
                type="checkbox"
                id={`partQualityIssue-${part.id}`}
                name="partQualityIssue"
                data-testid="mtm-part-row-quality-issue-checkbox"
                checked={!!part.qualityIssue}
                disabled={!isEditMode || !editable('partQualityIssue', part)}
                onChange={(event) =>
                  onPartFlag(part.id, 'qualityIssue', event.target.checked)
                }
              />
              <label
                htmlFor={`partQualityIssue-${part.id}`}
                style={{ marginBottom: 0 }}
                className={
                  !isEditMode || !editable('partQualityIssue', part)
                    ? 'text-disabled'
                    : ''
                }
              >
                <span className="sr-only">{t('orders.qualityIssue')}</span>
              </label>
            </div>
          </div>
          {pencil(
            'partQualityIssue',
            part,
            t('orders.qualityIssue'),
            'mtm-part-row-quality-issue-edit-button',
          )}
        </td>
        {statusMenu(part)}
        {pdfCells(group, part, index === 0)}
      </tr>
    );
  };

  return (
    <div className="table-container dms-parts">
      <table className="table table-header">
        <thead>
          <tr>
            <th style={{ width: '10%' }}>{t('orders.category')}</th>
            <th style={{ width: '12%' }}>{t('orders.sku')}</th>
            <th style={{ width: '9%' }}>{t('orders.fabric')}</th>
            <th style={{ width: '9%' }}>{t('orders.size')}</th>
            <th style={{ width: '8%' }}>{t('orders.quantity')}</th>
            <th style={{ width: '10%', whiteSpace: 'pre-line' }}>
              {t('orders.localPrice')}
            </th>
            {isAdmin && (
              <th style={{ width: '10%', whiteSpace: 'pre-line' }}>
                {t('orders.unitaryPriceEur')}
              </th>
            )}
            <th style={{ width: '18%', whiteSpace: 'pre-line' }}>
              {t('orders.supplierExpectedDeliveryDate')}
            </th>
            <th style={{ width: '15%', whiteSpace: 'pre-line' }}>
              {t('orders.deliveryDateInStore')}
            </th>
            <th style={{ width: '10%' }}>OA</th>
            <th style={{ width: '10%', whiteSpace: 'pre-line' }}>
              {t('orders.qualityIssue')}
            </th>
            <th style={{ width: '10%' }}>{t('orders.status')}</th>
            <th style={{ width: '7%', whiteSpace: 'pre-line' }}>
              {t('orders.purchaseOrder')}
            </th>
            <th style={{ width: '7%', whiteSpace: 'pre-line' }}>
              {t('orders.uploadPdf')}
            </th>
          </tr>
        </thead>
      </table>
      <div className="items-container" style={{ overflow: 'visible' }}>
        <table className="table items">
          <tbody>
            {groups.map((group) =>
              group.map((part, index) => [
                partRow(group, part, index),
                expanded[part.id] ? detailsRow(part) : null,
              ]),
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MeasurementCard({
  part,
  measurement,
  index,
  editable,
  isEditMode,
  onChange,
}: Readonly<{
  part: OrderPart;
  measurement: Measurement;
  index: number;
  editable: boolean;
  isEditMode: boolean;
  onChange: (patch: Partial<Measurement>) => void;
}>) {
  const { t } = useTranslation();
  const outOfLimit = measurementOutOfLimit(measurement);
  const image = measureImageUrl(part, measurement);
  const name = `${index}-${measurement.name}`;
  return (
    <div
      className="measurement-ctr"
      data-testid="mtm-part-details-measurement"
      data-label={measurement.name}
    >
      <div className="measurement-name">{measurement.name}</div>
      <div className="flex items-start">
        {image && <img src={image} alt="" />}
        {(measurement.min != null || measurement.totalMeasurement) && (
          <div className="ml-2">
            {measurement.min != null && (
              <div className={measurement.totalMeasurement ? '' : 'mb-2'}>
                {outOfLimit && (
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    style={{ color: 'darkorange' }}
                    aria-label="Out of limits"
                  />
                )}
                <div className="measure-value">
                  <div style={{ fontSize: 12 }}>alterations</div>
                  <div
                    className="view-item"
                    style={{ color: outOfLimit ? 'darkorange' : 'inherit' }}
                    data-testid="mtm-part-details-measurement-value"
                  >
                    {withSign(measurement.value)}
                  </div>
                  <input
                    type="number"
                    className="form-control edit-item"
                    name={`measurement-${name}`}
                    aria-label={`${measurement.name} alterations`}
                    data-testid="mtm-part-details-measurement-value-input"
                    readOnly={!editable}
                    value={
                      measurement.value == null ? '' : String(measurement.value)
                    }
                    onChange={(event) =>
                      onChange({ value: event.target.value })
                    }
                  />
                </div>
              </div>
            )}
            {measurement.totalMeasurement && (
              <div className="mb-2">
                <div className="measure-value">
                  <div style={{ fontSize: 12 }}>total</div>
                  <div
                    className="view-item"
                    data-testid="mtm-part-details-measurement-total"
                  >
                    {measurement.totalValue || ''}
                  </div>
                  <input
                    type="number"
                    className="form-control edit-item"
                    name={`measurement-total-${name}`}
                    aria-label={`${measurement.name} total`}
                    data-testid="mtm-part-details-measurement-total-input"
                    readOnly={!editable}
                    value={
                      measurement.totalValue == null
                        ? ''
                        : String(measurement.totalValue)
                    }
                    onChange={(event) =>
                      onChange({ totalValue: event.target.value })
                    }
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="mt-1">
        {(measurement.comments || (editable && isEditMode)) && (
          <small className="modal-label m-0">{t('orders.comments')}:</small>
        )}
        <span
          className="view-item"
          data-testid="mtm-part-details-measurement-comments"
        >
          {measurement.comments}
        </span>
        <input
          type="text"
          className="form-control edit-item"
          style={{ width: 100 }}
          name={`measurement-comments-${name}`}
          aria-label={`${measurement.name} comments`}
          data-testid="mtm-part-details-measurement-comments-input"
          readOnly={!editable}
          value={measurement.comments ?? ''}
          onChange={(event) => onChange({ comments: event.target.value })}
        />
      </div>
      {!!measurement.pictures?.length && (
        <div className="mt-2 measurement-pictures">
          {measurement.pictures.map((picture) => (
            <a
              key={picture}
              href={picture}
              target="_blank"
              rel="noreferrer"
              className="mx-1"
            >
              <img src={picture} alt={measurement.name} />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
